import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type Role } from '../../models/Role'
import { type BasePageRequest, type BasePageResponse, type BaseRequest, type BaseResponse } from '../models'
import { paginate } from '../utils'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface PermissionDefinition {
  id: string
  type: string
  description: string
}

export interface GetRolesRequest extends BasePageRequest {
  externalOnly?: boolean
}

export interface GetAllRolesRequest extends BaseRequest {
  externalOnly?: boolean
}

export const roleApi = createApi({
  reducerPath: 'roleApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  endpoints: (builder) => ({
    getRoles: builder.query<BasePageResponse<Role[]>, GetRolesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/roles`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            ...(request.externalOnly && { externalOnly: request.externalOnly }),
          },
        }
      },
    }),
    getAllRoles: builder.query<BaseResponse<Role[]>, GetAllRolesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<Role>(
          {
            url: `/accounts/${request.accountId}/roles`,
            params: {
              ...(request.externalOnly && { externalOnly: request.externalOnly }),
            },
          },
          fetchWithBQ,
        )
      },
    }),
    getPermissions: builder.query<PermissionDefinition[], void>({
      query: () => `/permissions`,
      transformResponse: (response: BaseResponse<PermissionDefinition[]>) => response.data,
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetRolesQuery, useGetAllRolesQuery, useGetPermissionsQuery } = roleApi
